import { ICategory, IMenu, IPotluck, IUser } from "interfaces";
import { Box, Stack } from "@mui/material";
import { Custom, Popup } from "components";
import { useApp, useNavigation, useTheme } from "contexts";
import { Combines, Validations } from "helpers";
import { useSummary } from "hooks";

// icons
import {
  ChevronLeftRounded,
  InfoOutlined,
  SendRounded,
} from "@mui/icons-material";

type Props = {
  potluck: IPotluck;
};

export const Summary = ({ potluck }: Props) => {
  const { t } = useApp();
  const { theme } = useTheme();
  const { goBack } = useNavigation();

  const {
    canEdit,
    menuIndex,
    currentCategory,
    currentPotluck,
    getCurrentCategories,
    getFilteredResponse,
    getSortedCategories,
    handleUpdateCategory,
    handleCancelCategory,
    manageItemOpened,
    setCategoryId,
    setMenuIndex,
    setManageItemOpened,
    setShareOpened,
    shareOpened,
    viewMenuOpened,
    viewResponseOpened,
    setViewMenuOpened,
    setViewResponseOpened,
  } = useSummary(potluck);

  return (
    <Box
      sx={{
        height: "auto",
        border: 1,
        overflow: "hidden",
        position: "relative",
        mt: theme.spacing.md,
        px: theme.spacing.lg,
        pt: theme.spacing.lg,
        pb: theme.spacing.xxl,
        borderRadius: theme.border.radius,
        borderColor: theme.color.primary.accent,
      }}
    >
      <Custom.Typography size={theme.font.md} weight={theme.font.bold}>
        {currentPotluck.party_name}
      </Custom.Typography>
      <Box mt={theme.spacing.md}>
        <Stack direction="row" spacing={theme.spacing.sm}>
          <Custom.Typography size={theme.font.xs} weight={theme.font.bold}>
            {t.component.workflow.summary.menu_theme}:
          </Custom.Typography>
          <Custom.Typography
            size={theme.font.xs}
            color={theme.color.font.accent}
          >
            {currentPotluck.theme}
          </Custom.Typography>
        </Stack>
      </Box>
      <Box mt={theme.spacing.md}>
        <Stack direction="row" spacing={theme.spacing.sm}>
          <Custom.Typography size={theme.font.xs} weight={theme.font.bold}>
            {t.component.workflow.summary.host}:
          </Custom.Typography>
          <Custom.Typography
            size={theme.font.xs}
            color={theme.color.font.accent}
          >
            {currentPotluck.created_by.full_name}
          </Custom.Typography>
        </Stack>
      </Box>
      <Box mt={theme.spacing.md}>
        <Stack spacing={theme.spacing.sm}>
          <Custom.Typography size={theme.font.xs} weight={theme.font.bold}>
            {t.component.workflow.summary.header}
          </Custom.Typography>
          <Custom.Typography
            size={theme.font.xs}
            color={theme.color.font.accent}
          >
            {t.component.workflow.summary.headline}
          </Custom.Typography>
        </Stack>
      </Box>
      <Box mt={theme.spacing.md}>
        <Stack spacing={theme.spacing.sm}>
          {currentPotluck.menu.map((menu: IMenu, i: number) => {
            return (
              <Box key={i} mt={theme.spacing.md}>
                <Stack spacing={theme.spacing.sm}>
                  <Box>
                    <Stack
                      width={1}
                      direction="row"
                      alignItems="center"
                      mr={theme.spacing.md}
                      mt={theme.spacing.md}
                      justifyContent="space-between"
                    >
                      <Custom.Typography
                        size={theme.font.xs}
                        weight={theme.font.semiBold}
                      >
                        {i + 1}. {menu.configuration.name} (
                        {menu.categories.length})
                      </Custom.Typography>
                      <Custom.Typography
                        size={theme.font.xs}
                        weight={theme.font.semiBold}
                      >
                        {t.component.workflow.summary.assignees}
                      </Custom.Typography>
                    </Stack>
                  </Box>

                  <Box>
                    <Stack gap={theme.spacing.sm}>
                      {getSortedCategories(menu)
                        .slice(0, menu.count)
                        .map((category: ICategory, j: number) => {
                          const isReplaced = Validations.hasValue(
                            category.replaced_with
                          );
                          const count = getFilteredResponse(
                            category,
                            true
                          ).length;
                          return (
                            <Box key={j} width={1}>
                              <Stack
                                direction="row"
                                alignItems="center"
                                justifyContent="space-between"
                              >
                                <Stack direction="row" gap={theme.spacing.xxs}>
                                  <Stack sx={{ ml: theme.spacing.xs }}>
                                    <Custom.Typography
                                      size={theme.font.xs}
                                      weight={theme.font.semiBold}
                                      sx={{
                                        textDecoration: isReplaced
                                          ? "line-through"
                                          : undefined,
                                      }}
                                    >
                                      {j + 1}. {category.name}
                                    </Custom.Typography>
                                    {!isReplaced && (
                                      <Stack
                                        direction="row"
                                        alignItems="center"
                                        spacing={theme.spacing.sm}
                                      >
                                        <Custom.Typography
                                          size={theme.font.xs}
                                          weight={theme.font.medium}
                                          color={theme.color.accent.color}
                                          sx={{
                                            textDecoration: isReplaced
                                              ? "line-through"
                                              : undefined,
                                          }}
                                        >
                                          {Combines.interpolate(
                                            t.message.responses,
                                            {
                                              count: count,
                                            }
                                          )}
                                        </Custom.Typography>
                                        {count > 0 && (
                                          <InfoOutlined
                                            sx={{
                                              width: 16,
                                              height: 16,
                                              cursor: "pointer",
                                              color: theme.color.accent.color,
                                            }}
                                            onClick={() => {
                                              setMenuIndex(i);
                                              setCategoryId(category._id);
                                              setViewResponseOpened(true);
                                            }}
                                          />
                                        )}
                                      </Stack>
                                    )}
                                    {isReplaced && (
                                      <Custom.Typography size={theme.font.xs}>
                                        {Combines.interpolate(
                                          t.message.replacement,
                                          {
                                            text: category.replaced_with,
                                          }
                                        )}
                                      </Custom.Typography>
                                    )}
                                  </Stack>
                                </Stack>
                                <Stack
                                  direction="row"
                                  alignItems="center"
                                  spacing={theme.spacing.sm}
                                >
                                  {category.assigned_to.length > 0 ? (
                                    <Custom.AvatarGroup max={2} size="small">
                                      {category.assigned_to.map(
                                        (item: IUser) => (
                                          <Custom.Avatar
                                            size="small"
                                            key={item._id}
                                            src={item._id}
                                            alt={item.first_name}
                                          />
                                        )
                                      )}
                                    </Custom.AvatarGroup>
                                  ) : (
                                    <Custom.Typography size={theme.font.xxs}>
                                      {t.label.unassigned}
                                    </Custom.Typography>
                                  )}
                                  {canEdit && (
                                    <Custom.Button
                                      size="small"
                                      trailing="edit"
                                      onClick={() => {
                                        setMenuIndex(i);
                                        setCategoryId(category._id);
                                        setManageItemOpened(true);
                                      }}
                                    >
                                      {t.action.edit}
                                    </Custom.Button>
                                  )}
                                </Stack>
                              </Stack>
                            </Box>
                          );
                        })}
                    </Stack>
                  </Box>

                  <Box>
                    <Custom.Button
                      size="small"
                      trailing="right"
                      onClick={() => {
                        setMenuIndex(i);
                        setViewMenuOpened(true);
                      }}
                    >
                      {t.component.workflow.summary.see_details} (
                      {menu.categories.length})
                    </Custom.Button>
                  </Box>
                </Stack>
              </Box>
            );
          })}
        </Stack>
        <Box mt={theme.spacing.sm}>
          <Stack direction="row" spacing={theme.spacing.xs}>
            <Custom.Button secondary onClick={goBack}>
              <ChevronLeftRounded />
            </Custom.Button>
            {canEdit && (
              <Custom.Button
                fullWidth
                endIcon={<SendRounded />}
                variant="contained"
                onClick={() => setShareOpened(true)}
              >
                {t.action.share_results}
              </Custom.Button>
            )}
          </Stack>
        </Box>
      </Box>
      <Popup.ManageItem
        open={manageItemOpened}
        menu={currentPotluck.menu[menuIndex]}
        participants={currentPotluck.participants}
        category={getCurrentCategories}
        onClose={() => setManageItemOpened(false)}
        onUpdate={handleUpdateCategory}
        onCancel={handleCancelCategory}
      />
      <Popup.ViewMenu
        open={viewMenuOpened}
        categories={getSortedCategories(currentPotluck.menu[menuIndex])}
        onClose={() => setViewMenuOpened(false)}
      />
      <Popup.Share
        id={currentPotluck._id}
        open={shareOpened}
        participants={currentPotluck.participants}
        onClose={() => setShareOpened(false)}
      />
      <Popup.ViewResponse
        open={viewResponseOpened}
        name={currentCategory?.name}
        responses={
          currentCategory ? getFilteredResponse(currentCategory, true) : []
        }
        onClose={() => setViewResponseOpened(false)}
      />
    </Box>
  );
};
